import React from "react";
import { FONT, T } from "./tokens";

export function Chip({ x, y, w, h, theme, lines }) {
  const rows = Array.isArray(lines) ? lines : [lines];
  const long = rows.some((line) => String(line).length > 18);
  const fs = rows.length > 1 || long ? 11 : 12;
  const lh = 14;
  const start = y + h / 2 - ((rows.length - 1) * lh) / 2 + fs / 2.6;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx="8"
        fill={T.chip}
        stroke={theme.line}
        strokeOpacity="0.85"
        strokeWidth="1.15"
      />
      {rows.map((line, i) => (
        <text
          key={`${line}-${i}`}
          x={x + w / 2}
          y={start + i * lh}
          textAnchor="middle"
          fill={theme.text}
          fontSize={fs}
          fontWeight="600"
          fontFamily={FONT}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

export function Layer({ x, y, w, h, theme, title, children }) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx="14"
        fill={theme.fill}
        stroke={theme.line}
        strokeOpacity="0.9"
        strokeWidth="1.15"
      />
      <text
        x={x + w / 2}
        y={y + 24}
        textAnchor="middle"
        fill={theme.text}
        fontSize="11"
        fontWeight="700"
        letterSpacing="0.14em"
        fontFamily={FONT}
      >
        {title}
      </text>
      {children}
    </g>
  );
}

export function Chevron({ x, y }) {
  return (
    <g>
      <line
        x1={x}
        y1={y - 5}
        x2={x}
        y2={y + 2}
        stroke={T.arrow}
        strokeWidth="1.4"
      />
      <path
        d={`M ${x - 8} ${y + 2} L ${x} ${y + 12} L ${x + 8} ${y + 2} Z`}
        fill={T.arrow}
      />
    </g>
  );
}

export function DiagramShell({ uid, viewBox, title, subtitle, label, children }) {
  const [, , w] = viewBox.split(" ").map(Number);
  const bg = `${uid}-bg`;
  return (
    <svg
      viewBox={viewBox}
      role="img"
      aria-label={label}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={bg} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d0d10" />
          <stop offset="100%" stopColor={T.canvas} />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${bg})`} />
      <text
        x={w / 2}
        y="34"
        textAnchor="middle"
        fill={T.ink}
        fontSize="18"
        fontWeight="700"
        letterSpacing="-0.03em"
        fontFamily={FONT}
      >
        {title}
      </text>
      {subtitle && (
        <text
          x={w / 2}
          y="54"
          textAnchor="middle"
          fill={T.muted}
          fontSize="12"
          fontWeight="500"
          fontFamily={FONT}
        >
          {subtitle}
        </text>
      )}
      {children}
    </svg>
  );
}

export function rowChips(items, { x, y, w, h, gap, theme }) {
  const n = items.length;
  const cw = (w - gap * (n - 1)) / n;
  return items.map((item, i) => (
    <Chip
      key={Array.isArray(item) ? item.join(" ") : item}
      x={x + i * (cw + gap)}
      y={y}
      w={cw}
      h={h}
      theme={theme}
      lines={item}
    />
  ));
}

export function gridChips(items, { x, y, w, cols, h, gap, theme }) {
  const cw = (w - gap * (cols - 1)) / cols;
  let col = 0;
  let row = 0;
  return items.map((item) => {
    const span = item.span || 1;
    if (col + span > cols) {
      row += 1;
      col = 0;
    }
    const width = span === cols ? w : cw * span + gap * (span - 1);
    const lines = item.lines || item;
    const node = (
      <Chip
        key={Array.isArray(lines) ? lines.join(" ") : lines}
        x={x + col * (cw + gap)}
        y={y + row * (h + gap)}
        w={width}
        h={h}
        theme={theme}
        lines={lines}
      />
    );
    col += span;
    if (col >= cols) {
      col = 0;
      row += 1;
    }
    return node;
  });
}
